import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { CheckCircle2, ArrowRight, Zap, Shield, TrendingUp, Users, BarChart3, RefreshCw, Brain, Target } from 'lucide-react';

const useCaseData: Record<string, {
  title: string;
  headline: string;
  description: string;
  seoTitle: string;
  seoDescription: string;
  keywords: string;
  icon: React.ElementType;
  problem: string;
  solution: string;
  benefits: string[];
  howItWorks: { step: string; detail: string }[];
  stats: { value: string; label: string }[];
  cta: string;
}> = {
  'deal-forecasting': {
    title: 'Evidence-Based Deal Forecasting',
    headline: 'Forecasts your board can actually defend',
    description: 'NoQ5 replaces gut-feel forecasting with evidence-based predictions grounded in real buyer signals, email engagement, and meeting activity.',
    seoTitle: 'AI Deal Forecasting for Salesforce & HubSpot | NoQ5',
    seoDescription: 'Stop guessing your pipeline. NoQ5 delivers evidence-based deal forecasts using real buyer signals from email, calendar, and CRM data. Built for revenue teams.',
    keywords: 'AI deal forecasting, evidence-based forecasting, pipeline prediction, sales forecasting AI, CRM forecasting tool, revenue forecasting',
    icon: BarChart3,
    problem: 'Most forecasts are built on rep opinions, not evidence. CRO-reported forecasts miss by 20-40% because they rely on stage names and close dates that reps rarely update.',
    solution: 'NoQ5 scores every deal using real evidence: email reply velocity, meeting frequency, multi-threading depth, and buying committee engagement. No manual input required.',
    benefits: [
      'Forecast accuracy improves 30-50% within the first quarter',
      'Evidence-based scoring removes bias from pipeline reviews',
      'Automatic deal categorization into Commit, Best Case, and Upside',
      'Board-ready reports generated in seconds',
      'Historical backtest validates model accuracy against your own data',
    ],
    howItWorks: [
      { step: 'Connect', detail: 'Link your CRM, email, and calendar. NoQ5 ingests all deal activity automatically.' },
      { step: 'Score', detail: 'Every deal gets an evidence score based on 15+ buyer engagement signals.' },
      { step: 'Forecast', detail: 'AI categorizes deals into forecast buckets with confidence levels.' },
      { step: 'Defend', detail: 'Present board-ready forecasts with evidence trails for every number.' },
    ],
    stats: [
      { value: '35%', label: 'Forecast accuracy improvement' },
      { value: '4hrs', label: 'Saved per week on pipeline reviews' },
      { value: '90%', label: 'Deals scored automatically' },
    ],
    cta: 'Start forecasting with evidence',
  },
  'crm-automation': {
    title: 'Autonomous CRM Updates',
    headline: 'Your CRM updates itself. Finally.',
    description: 'NoQ5 captures deal activity from emails, meetings, and calls, then writes it back to your CRM automatically. Reps never touch a field again.',
    seoTitle: 'AI CRM Automation for Salesforce & HubSpot | NoQ5',
    seoDescription: 'Automate CRM data entry with AI. NoQ5 captures deal activity from emails, meetings, and calls, then updates Salesforce or HubSpot automatically. GDPR-compliant.',
    keywords: 'CRM automation AI, automatic CRM updates, Salesforce automation, HubSpot automation, CRM data entry AI, auto-update CRM',
    icon: RefreshCw,
    problem: 'Sales reps spend 5+ hours per week on CRM data entry. 70% of deal data is incomplete or outdated. This kills forecast accuracy and pipeline visibility.',
    solution: 'NoQ5 listens to your communication channels (email, calendar, Slack) and automatically logs activities, updates deal stages, and enriches contact records in your CRM.',
    benefits: [
      'Eliminate 5+ hours of manual data entry per rep per week',
      'CRM data completeness jumps from 30% to 95%+',
      'Deal stages update automatically based on real buyer signals',
      'Contact records enriched from email signatures and meeting context',
      'Full audit trail for every automated change',
    ],
    howItWorks: [
      { step: 'Connect', detail: 'Link your CRM and communication tools in under 5 minutes.' },
      { step: 'Capture', detail: 'NoQ5 monitors emails, meetings, and messages for deal-relevant activity.' },
      { step: 'Update', detail: 'CRM records are updated automatically with proper attribution.' },
      { step: 'Verify', detail: 'Every change is logged with a full audit trail. Undo anything in one click.' },
    ],
    stats: [
      { value: '5hrs', label: 'Saved per rep per week' },
      { value: '95%', label: 'CRM data completeness' },
      { value: '<2min', label: 'Setup time per integration' },
    ],
    cta: 'Automate your CRM today',
  },
  'pipeline-management': {
    title: 'AI Pipeline Management',
    headline: 'See your pipeline as it really is',
    description: 'NoQ5 gives revenue leaders real-time pipeline intelligence with deal health scores, risk alerts, and coverage analysis. No more guessing.',
    seoTitle: 'AI Pipeline Management for Revenue Teams | NoQ5',
    seoDescription: 'Real-time pipeline intelligence with AI deal health scores, risk alerts, and coverage analysis. Built for revenue leaders using Salesforce or HubSpot.',
    keywords: 'AI pipeline management, deal health scoring, pipeline intelligence, revenue operations AI, deal risk alerts, pipeline coverage',
    icon: TrendingUp,
    problem: 'Pipeline reviews are backward-looking. By the time you spot a deal going dark, it is already lost. Revenue leaders need real-time signals, not weekly spreadsheets.',
    solution: 'NoQ5 continuously monitors buyer engagement across all channels and flags deals that are going dark, stalling, or showing positive momentum shifts.',
    benefits: [
      'Real-time deal health scores updated every hour',
      'Going-dark alerts before deals slip',
      'Pipeline coverage analysis against targets',
      'Rep-by-rep performance insights',
      'Weekly pipeline summary reports delivered to Slack',
    ],
    howItWorks: [
      { step: 'Connect', detail: 'Integrate your CRM and communication stack.' },
      { step: 'Monitor', detail: 'NoQ5 tracks buyer engagement signals across every deal.' },
      { step: 'Alert', detail: 'Get proactive alerts when deals show risk signals.' },
      { step: 'Act', detail: 'Take action on AI-recommended next steps for at-risk deals.' },
    ],
    stats: [
      { value: '25%', label: 'More pipeline saved from going dark' },
      { value: '2x', label: 'Faster deal risk identification' },
      { value: '60%', label: 'Less time in pipeline review meetings' },
    ],
    cta: 'Get real-time pipeline intelligence',
  },
  'business-continuity': {
    title: 'Sales Business Continuity',
    headline: 'When reps leave, deals stay',
    description: 'NoQ5 ensures institutional knowledge survives rep turnover. Every deal interaction is captured, indexed, and transferable.',
    seoTitle: 'Sales Business Continuity & Rep Turnover Protection | NoQ5',
    seoDescription: 'Protect pipeline value when reps leave. NoQ5 captures every deal interaction so knowledge transfers instantly. No more lost deals from turnover.',
    keywords: 'sales business continuity, rep turnover, deal handoff, sales knowledge management, pipeline protection, rep transition',
    icon: Shield,
    problem: 'When a rep leaves, 30% of their pipeline value walks out the door. Deal context, relationship history, and buyer preferences live in their head, not your CRM.',
    solution: 'NoQ5 continuously captures every interaction, meeting note, email thread, and buyer signal. When a rep transitions, their entire deal context transfers instantly.',
    benefits: [
      'Zero pipeline value lost during rep transitions',
      'Complete deal history available for new reps on day one',
      'Automatic relationship mapping across buying committees',
      'Meeting summaries and key decisions preserved',
      'Onboarding time for new reps reduced by 60%',
    ],
    howItWorks: [
      { step: 'Capture', detail: 'Every deal interaction is automatically logged and indexed.' },
      { step: 'Preserve', detail: 'Deal context, buyer preferences, and relationship maps are stored.' },
      { step: 'Transfer', detail: 'When reps transition, full deal context transfers to the new owner.' },
      { step: 'Onboard', detail: 'New reps get AI-generated briefings on every deal they inherit.' },
    ],
    stats: [
      { value: '30%', label: 'Pipeline value typically lost to turnover' },
      { value: '0%', label: 'Lost with NoQ5 continuity' },
      { value: '60%', label: 'Faster new rep onboarding' },
    ],
    cta: 'Protect your pipeline today',
  },
  'sales-coaching': {
    title: 'AI Sales Coaching',
    headline: 'Every rep gets a personal coach',
    description: 'NoQ5 delivers personalized coaching nudges based on real deal activity. Spot what top performers do differently and replicate it across the team.',
    seoTitle: 'AI Sales Coaching for Revenue Teams | NoQ5',
    seoDescription: 'Personalized AI coaching for every sales rep. NoQ5 analyzes deal patterns, identifies winning behaviors, and delivers coaching nudges in real-time.',
    keywords: 'AI sales coaching, sales coaching tool, rep coaching AI, deal coaching, sales performance, win rate improvement',
    icon: Brain,
    problem: 'Sales managers cannot coach every deal. They see 10% of what happens and rely on reps to self-report. Best practices stay locked in top performers\' habits.',
    solution: 'NoQ5 analyzes patterns across all deals and reps, identifying what top performers do differently. It delivers coaching nudges to reps at the right moment in the deal cycle.',
    benefits: [
      'Personalized coaching based on individual deal patterns',
      'Best practices automatically identified from top performers',
      'Real-time nudges delivered via Slack or email',
      'Manager dashboards showing coaching opportunities',
      'Win/loss analysis with actionable recommendations',
    ],
    howItWorks: [
      { step: 'Analyze', detail: 'NoQ5 studies deal patterns across your entire team.' },
      { step: 'Identify', detail: 'Top performer behaviors and best practices are surfaced.' },
      { step: 'Coach', detail: 'Personalized nudges delivered to reps at key deal moments.' },
      { step: 'Improve', detail: 'Track coaching impact on win rates and deal velocity.' },
    ],
    stats: [
      { value: '20%', label: 'Win rate improvement' },
      { value: '3x', label: 'More coaching moments identified' },
      { value: '15%', label: 'Shorter deal cycles' },
    ],
    cta: 'Start coaching with AI',
  },
  'revenue-intelligence': {
    title: 'Revenue Intelligence Platform',
    headline: 'Intelligence that drives revenue, not dashboards',
    description: 'NoQ5 is the AI revenue intelligence platform that turns scattered CRM data into actionable insights. Built for revenue teams with a sales motion.',
    seoTitle: 'AI Revenue Intelligence Platform | NoQ5',
    seoDescription: 'Turn CRM data into revenue. NoQ5 AI revenue intelligence platform delivers forecasting, coaching, and pipeline insights for Salesforce & HubSpot. GDPR-compliant.',
    keywords: 'revenue intelligence platform, AI revenue intelligence, revenue operations, RevOps AI, sales intelligence, CRM intelligence',
    icon: Target,
    problem: 'Revenue teams drown in dashboards but starve for insights. Data is scattered across CRM, email, calendar, and Slack. No single view of what is really happening.',
    solution: 'NoQ5 unifies all revenue data into one AI-powered intelligence layer. It connects to your existing tools and delivers actionable insights without building another dashboard.',
    benefits: [
      'Single source of truth for all revenue data',
      'AI-generated insights, not just charts',
      'Natural language queries for any revenue question',
      'Automatic anomaly detection and alerts',
      'Works with your existing Salesforce or HubSpot setup',
    ],
    howItWorks: [
      { step: 'Connect', detail: 'Link CRM, email, calendar, and communication tools.' },
      { step: 'Unify', detail: 'NoQ5 creates a unified revenue data layer.' },
      { step: 'Analyze', detail: 'AI continuously analyzes patterns and anomalies.' },
      { step: 'Act', detail: 'Get actionable recommendations delivered where you work.' },
    ],
    stats: [
      { value: '10x', label: 'Faster revenue insights' },
      { value: '95%', label: 'Data coverage vs 30% manual' },
      { value: '€0', label: 'Additional BI tools needed' },
    ],
    cta: 'Get revenue intelligence now',
  },
};

const UseCasePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const useCase = slug ? useCaseData[slug] : null;

  if (!useCase) {
    return (
      <Layout>
        <SEOHead 
          title="AI Use Cases for Revenue Teams | NoQ5"
          description="Explore how NoQ5 AI helps revenue teams with forecasting, CRM automation, pipeline management, coaching, and business continuity."
          canonicalUrl="https://noq5.ai/use-cases"
        />
        <div className="min-h-screen bg-background pt-24 pb-16">
          <div className="container mx-auto px-4 max-w-6xl">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6 text-center">AI Use Cases for Revenue Teams</h1>
            <p className="text-xl text-muted-foreground text-center mb-12 max-w-3xl mx-auto">
              See how NoQ5 helps revenue teams automate CRM operations, forecast with evidence, and scale without headcount.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Object.entries(useCaseData).map(([key, uc]) => {
                const Icon = uc.icon;
                return (
                  <Link key={key} to={`/use-cases/${key}`} className="group">
                    <div className="border border-border rounded-sm p-6 bg-card hover:border-primary/50 transition-all hover:shadow-lg">
                      <Icon className="w-10 h-10 text-primary mb-4" />
                      <h2 className="text-xl font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">{uc.title}</h2>
                      <p className="text-muted-foreground text-sm">{uc.description}</p>
                      <span className="inline-flex items-center text-primary text-sm mt-4 font-medium">
                        Learn more <ArrowRight className="w-4 h-4 ml-1" />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const Icon = useCase.icon;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": useCase.seoTitle,
      "description": useCase.seoDescription,
      "url": `https://noq5.ai/use-cases/${slug}`,
      "publisher": {
        "@type": "Organization",
        "name": "NoQ5.ai",
        "url": "https://noq5.ai"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": useCase.title,
      "description": useCase.solution,
      "step": useCase.howItWorks.map((s, i) => ({
        "@type": "HowToStep",
        "position": i + 1,
        "name": s.step,
        "text": s.detail
      }))
    }
  ];

  return (
    <Layout>
      <SEOHead 
        title={useCase.seoTitle}
        description={useCase.seoDescription}
        keywords={useCase.keywords}
        canonicalUrl={`https://noq5.ai/use-cases/${slug}`}
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-background pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Hero */}
          <div className="text-center mb-16">
            <Icon className="w-16 h-16 text-primary mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">{useCase.headline}</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">{useCase.description}</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-6 mb-16">
            {useCase.stats.map((stat, i) => (
              <div key={i} className="text-center p-6 bg-card border border-border rounded-sm">
                <div className="text-3xl font-bold text-primary">{stat.value}</div>
                <div className="text-sm text-muted-foreground mt-1">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Problem / Solution */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="p-6 bg-destructive/5 border border-destructive/20 rounded-sm">
              <h2 className="text-xl font-semibold text-foreground mb-3">The Problem</h2>
              <p className="text-muted-foreground">{useCase.problem}</p>
            </div>
            <div className="p-6 bg-primary/5 border border-primary/20 rounded-sm">
              <h2 className="text-xl font-semibold text-foreground mb-3">The NoQ5 Solution</h2>
              <p className="text-muted-foreground">{useCase.solution}</p>
            </div>
          </div>

          {/* Benefits */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-foreground mb-6">Key Benefits</h2>
            <ul className="space-y-4">
              {useCase.benefits.map((benefit, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <span className="text-foreground">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* How It Works */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-foreground mb-6">How It Works</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {useCase.howItWorks.map((step, i) => (
                <div key={i} className="p-6 bg-card border border-border rounded-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">{i + 1}</span>
                    <h3 className="text-lg font-semibold text-foreground">{step.step}</h3>
                  </div>
                  <p className="text-muted-foreground">{step.detail}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center p-8 bg-primary/5 border border-primary/20 rounded-sm">
            <h2 className="text-2xl font-bold text-foreground mb-4">{useCase.cta}</h2>
            <p className="text-muted-foreground mb-6">See how NoQ5.ai works with your revenue process and existing systems.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/?scrollTo=demo">
                <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                  Book a Demo <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="lg" variant="outline">View Pricing</Button>
              </Link>
            </div>
          </div>

          {/* Related Use Cases */}
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-foreground mb-6">Related Use Cases</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {Object.entries(useCaseData)
                .filter(([key]) => key !== slug)
                .slice(0, 3)
                .map(([key, uc]) => {
                  const RelIcon = uc.icon;
                  return (
                    <Link key={key} to={`/use-cases/${key}`} className="group p-4 border border-border rounded-sm hover:border-primary/50 transition-all">
                      <RelIcon className="w-6 h-6 text-primary mb-2" />
                      <h3 className="font-medium text-foreground group-hover:text-primary transition-colors text-sm">{uc.title}</h3>
                    </Link>
                  );
                })}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default UseCasePage;
